import PCA from "ml-pca";

/**
 * Perform Principal Component Analysis (PCA) on input data.
 *
 * @param {Array<Array<number>>} data - Input data, where each inner array represents a single data point.
 * @param {number} [dim=3] - Number of dimensions to reduce to.
 * @returns {{pcaResult: { pcaReduced: Array<Array<number>>, pcaMatrix: Array<Array<number>>, pcaMeanVector: Array<number>, pcaMeanVectorDimensions: number }}} - Object containing the PCA result.
 */
export function performPCA(data, dim = 3) {
  console.log(
    `[embeddingUtils:performPCA] Performing PCA on data with shape: ${data.length}x${data[0].length}, reducing to ${dim} dimensions`
  );
  if (data.length === 0 || data[0].length <= dim) {
    console.log(
      `[embeddingUtils:performPCA] Data has no rows or fewer columns than requested dimensions (${dim}), returning original data.`
    );
    return {
      pcaResult: {
        pcaReduced: data,
        pcaMatrix: null,
        pcaMeanVector: null,
        pcaMeanVectorDimensions: 0,
      },
    };
  }

  if (!Array.isArray(data) || !data.every(Array.isArray)) {
    throw new Error("Data must be a 2D array of numbers.");
  }

  const pca = new PCA(data, { center: true, scale: false });
  const pcaReduced = pca.predict(data, { nComponents: dim }).to2DArray();
  const pcaMatrixFull = pca.getLoadings().to2DArray();
  const pcaMatrix = pcaMatrixFull.slice(0, dim);
  const pcaMeanVector = pca.means;
  const pcaMeanVectorDimensions = pca.means.length;
  console.log(
    `[embeddingUtils:performPCA] PCA completed. Reduced data shape: ${pcaReduced.length}x${pcaReduced[0].length}, PCA matrix shape: ${pcaMatrix.length}x${pcaMatrix[0].length}, Mean vector dimensions: ${pcaMeanVectorDimensions}`
  );

  return {
    pcaResult: {
      pcaReduced,
      pcaMatrix,
      pcaMeanVector,
      pcaMeanVectorDimensions,
    },
  };
}

/**
 * Fetch all embeddings for chunks with concurrency cap.
 *
 * @param {Array<{chunk: string}>} chunks - Array of chunk objects with a "chunk" property.
 * @param {function(string): Promise<number[]>} fetchEmbeddingForChunk - Function to fetch a single embedding for a given chunk.
 * @param {number} [concurrency=6] - Number of concurrent workers to use for fetching embeddings.
 * @returns {Promise<Array<Array<number>>>} - Promise resolving to an array of embedding vectors, one for each chunk in the input array.
 */
export async function fetchEmbeddingsForChunks(
  chunks,
  fetchEmbeddingForChunk,
  concurrency = 6
) {
  const results = new Array(chunks.length);
  let nextIndex = 0;

  /**
   * Worker function to fetch a single embedding for a given chunk.
   * Called by multiple concurrent workers to fetch all embeddings in parallel.
   *
   * @throws {Error} If fetching the embedding fails.
   */
  async function worker() {
    while (true) {
      const idx = nextIndex++;
      if (idx >= chunks.length) break;
      try {
        results[idx] = await fetchEmbeddingForChunk(chunks[idx].chunkText);
      } catch (err) {
        console.error(
          `[embeddingUtils:fetchEmbeddingsForChunks] Error fetching embedding for chunk ${idx}:`,
          err
        );
        throw err;
      }
    }
  }

  const workers = Array.from({ length: concurrency }, () => worker());
  await Promise.all(workers);
  return results;
}
